import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { scrollToSection } from '../Section/ScrollToSection';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const TOTAL_EGGS = 22;
const STORAGE_KEY = 'rr_found_eggs';

const KONAMI = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];

// ─── CONSOLE SECRET MESSAGE ───────────────────────────────────────────────────
function injectConsoleMessage() {
  const styles = {
    title: 'font-size:28px;font-weight:900;color:#06b6d4;text-shadow:0 0 20px #06b6d460;letter-spacing:4px;',
    sub: 'font-size:12px;color:#94a3b8;font-family:monospace;line-height:1.8;',
    accent: 'font-size:12px;color:#22d3ee;font-family:monospace;font-weight:bold;',
    warn: 'font-size:11px;color:#f59e0b;font-family:monospace;',
  };
  /* eslint-disable no-console */
  console.log('%cRITESH RANJAN', styles.title);
  console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', styles.sub);
  console.log('%c🖐️  Hey dev! Nice to meet a curious one.', styles.sub);
  console.log('%cSince you\'re snooping around… I like your style.', styles.sub);
  console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', styles.sub);
  console.log('%c📬  ranjanritesh0309@gmail.com', styles.accent);
  console.log('%c🔗  linkedin.com/in/ritesh-ranjan09', styles.accent);
  console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', styles.sub);
  console.log('%c⚠️  You found a hidden dev egg! Keep exploring 🥚', styles.warn);
  /* eslint-enable no-console */
}

// ─── MANGEKYOU SHARINGAN (ITACHI) ─────────────────────────────────────────────
function MangekouEgg({ onDone }) {
  const [phase, setPhase] = useState(0);
  // Phase timeline:
  //  0 → 300ms  : black bg fades in
  //  1 → 1100ms : red corner bleeds
  //  2 → 2600ms : eye scales in + first rotation begins (5s/rev)
  //  3 → 7600ms : AMATERASU text appears
  //  4 → 8200ms : "enter the eye" — scale explodes 1x→30x + faster spin
  //  onDone at  : 12000ms (after screen fully red → black fade)
  useEffect(() => {
    // t2 eye appears at 1100ms. First rotation = 1.5s easeInOut.
    // 0.2s pause. Then expansion at 1100+1500+200 = 2800ms.
    const t1 = setTimeout(() => setPhase(1), 300);
    const t2 = setTimeout(() => setPhase(2), 1100);
    const t3 = setTimeout(() => setPhase(3), 2800);  // after 1 easeInOut spin + 0.2s wait
    const t4 = setTimeout(onDone, 4300);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, [onDone]);

  // ── ROTATION SEQUENCE ──────────────────────────────────────────────────────
  // useAnimation lets us sequence: easeInOut (1.5s) → pause 200ms → linear repeat
  const bladeControls = useAnimation();
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // Wait for phase 2 (1100ms)
      await new Promise(res => setTimeout(res, 1100));
      if (cancelled) return;
      
      // First rotation: easeInOut — slow start, slow end
      await bladeControls.start({ rotate: 360, transition: { duration: 1.5, ease: 'easeInOut' } });
      if (cancelled) return;
      
      // Reset to 0 (visually identical) before repeating
      bladeControls.set({ rotate: 0 });
      
      // 0.2s pause before the zoom-in spin
      await new Promise(res => setTimeout(res, 200));
      if (cancelled) return;
      
      // Second rotation: fast linear continuous
      bladeControls.start({ rotate: 360, transition: { duration: 0.7, ease: 'linear', repeat: Infinity } });
    };
    run();
    return () => { cancelled = true; bladeControls.stop(); };
  }, [bladeControls]);

  const eyeSize = typeof window !== 'undefined' && window.innerWidth < 600 ? 270 : 320;

  return (
    <motion.div
      className="fixed inset-0 z-[9998] bg-black flex items-center justify-center overflow-hidden cursor-pointer"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onDone}
    >
      {/* ── RED CORNER BLEEDS ── */}
      {phase >= 1 && [
        ['top-0 left-0',     'top left'],
        ['top-0 right-0',    'top right'],
        ['bottom-0 left-0',  'bottom left'],
        ['bottom-0 right-0', 'bottom right'],
      ].map(([cls, origin], i) => (
        <motion.div
          key={i}
          className={`absolute ${cls} w-[70vw] h-[70vh]`}
          style={{ background: `radial-gradient(ellipse at ${origin}, rgba(140,0,0,0.95) 0%, rgba(70,0,0,0.5) 45%, transparent 70%)` }}
          initial={{ scale: 0, opacity: 0 }}
          animate={phase >= 3
            ? { scale: 12, opacity: 1 }   // expand with the eye
            : { scale: 2.5, opacity: 1 }
          }
          transition={phase >= 3
            ? { duration: 0.9, ease: 'easeIn' }
            : { duration: 1.3, ease: [0.16, 1, 0.3, 1], delay: i * 0.07 }
          }
        />
      ))}

      {/* ── THE EYE CONTAINER (scales up in phase 4) ── */}
      {phase >= 2 && (
        <motion.div
          className="relative z-10"
          initial={{ scale: 0, opacity: 0 }}
          animate={phase >= 3
            ? { scale: 30, opacity: 1 }          // fills entire screen
            : { scale: 1,  opacity: 1 }
          }
          transition={phase >= 3
            ? { duration: 0.9, ease: [0.4, 0, 1, 1] }  // 0.9s expansion
            : { duration: 1.0, ease: [0.16, 1, 0.3, 1] }
          }
        >
          {/* Outer pulsing halo — hidden during expansion */}
          {phase < 4 && (
            <motion.div
              className="absolute rounded-full pointer-events-none"
              style={{ inset: -36 }}
              animate={{ boxShadow: [
                '0 0 45px 22px rgba(170,0,0,0.5)',
                '0 0 120px 70px rgba(220,0,0,1)',
                '0 0 45px 22px rgba(170,0,0,0.5)',
              ]}}
              transition={{ duration: 2, repeat: Infinity }}
            />
          )}

          <svg
            viewBox="-100 -100 200 200"
            width={eyeSize}
            height={eyeSize}
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <filter id="mglow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <radialGradient id="irisG" cx="0" cy="0" r="82" gradientUnits="userSpaceOnUse">
                <stop offset="0%"   stopColor="#ff4400" />
                <stop offset="28%"  stopColor="#ee1a00" />
                <stop offset="62%"  stopColor="#cc0800" />
                <stop offset="100%" stopColor="#4a0000" />
              </radialGradient>
            </defs>

            {/* Dark outer frame ring */}
            <circle r="90" fill="#040000" />

            {/* RED IRIS */}
            <circle r="82" fill="url(#irisG)" filter="url(#mglow)" />

            {/* Blade rotation controlled by useAnimation (easeInOut once → pause → linear repeat) */}
            <motion.g
              initial={{ rotate: 0 }}
              animate={bladeControls}
              style={{ transformBox: 'view-box', transformOrigin: '50% 50%' }}
            >
              {/* Blade inner tip moved to r≈16 (pupil circumference) so blades connect at center */}
              {/* A=(14,8) r=√(196+64)=√260≈16.1, outer tip B=(-18,-80) r≈82 */}
              {[0, 120, 240].map(angle => (
                <path
                  key={angle}
                  d="M 14,8 Q 75,-10 -18,-80 Q 5,-15 14,8 Z"
                  fill="#070000"
                  transform={`rotate(${angle})`}
                />
              ))}
            </motion.g>

            {/* Border strokes on top */}
            <circle r="82" fill="none" stroke="#0c0000" strokeWidth="3" />
            <circle r="90" fill="none" stroke="#0f0000" strokeWidth="3" />

            {/* Central pupil — drawn LAST so it seals the gap between the 3 blade inner tips */}
            {/* r=20 to ensure overlap with blade tips at r≈16, no red slivers visible */}
            <circle r="20" fill="#030000" filter="url(#mglow)" />
            <circle r="14" fill="#1a0000" />
            <circle r="7"  fill="#380000" />
            <circle r="3"  cx="-3" cy="-4" fill="#700000" opacity="0.8" />
          </svg>
        </motion.div>
      )}

      {/* ── AMATERASU TEXT (appears with expansion) ── */}
      {phase >= 3 && (
        <motion.div
          className="absolute bottom-[16%] text-center pointer-events-none z-20"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <p
            className="font-black tracking-[0.8em] uppercase"
            style={{
              fontSize: 22,
              color: '#cc2200',
              fontFamily: 'Georgia, serif',
              textShadow: '0 0 20px rgba(220,0,0,1), 0 0 50px rgba(180,0,0,0.7)',
            }}
          >
            AMATERASU
          </p>
          <p className="text-red-900/40 font-mono text-[10px] tracking-widest mt-1">
            TAP ANYWHERE TO DISMISS
          </p>
        </motion.div>
      )}

      {/* ── BLACK FADE out after expansion ── */}
      {phase >= 3 && (
        <motion.div
          className="fixed inset-0 z-30 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0, 0.7, 1] }}
          transition={{ duration: 1.2, times: [0, 0.55, 0.78, 1], ease: 'easeIn' }}
          style={{ background: '#000' }}
        />
      )}
    </motion.div>
  );
}



// ─── MATRIX BACKGROUND CANVAS ────────────────────────────────────────────────
function MatrixBackground() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const cols = Math.floor(canvas.width / 14);
    const drops = Array(cols).fill(1);
    const chars = '01RITESHRANJANMATRIXHACKDEV'.split('');

    const draw = () => {
      ctx.fillStyle = 'rgba(0,0,0,0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#00ff9d';
      ctx.font = '13px monospace';
      for (let i = 0; i < drops.length; i++) {
        const char = chars[Math.floor(Math.random() * chars.length)];
        ctx.fillText(char, i * 14, drops[i] * 14);
        if (drops[i] * 14 > canvas.height && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      }
    };

    const interval = setInterval(draw, 35);
    return () => clearInterval(interval);
  }, []);
  
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}

// ─── MATRIX RAIN EASTER EGG ──────────────────────────────────────────────────
function MatrixRain({ onDone }) {
  useEffect(() => {
    const timeout = setTimeout(onDone, 4000);
    return () => clearTimeout(timeout);
  }, [onDone]);

  return (
    <motion.div
      className="fixed inset-0 z-[9998] bg-black"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <MatrixBackground />
      <div className="absolute inset-0 flex items-end justify-center pb-12">
        <div className="px-8 py-4 bg-black/80 border border-[#00ff9d]/30 rounded-2xl backdrop-blur-sm">
          <p className="font-mono text-[#00ff9d] text-xs tracking-[0.3em] uppercase text-center animate-pulse">
            ACCESS GRANTED — Welcome, fellow developer
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ─── HACKER TERMINAL ─────────────────────────────────────────────────────────
function HackerTerminal({ onDone }) {
  const [lines, setLines] = useState([]);
  const [showTerminal, setShowTerminal] = useState(false);

  useEffect(() => {
    // Wait for 1.5 seconds of just matrix rain falling before showing the terminal window
    const renderTerminalTimeout = setTimeout(() => {
      setShowTerminal(true);
    }, 1500);
    
    return () => clearTimeout(renderTerminalTimeout);
  }, []);

  useEffect(() => {
    if (!showTerminal) return;
    
    const seq = [
      '> Accessing portfolio...',
      '> Scanning skill database...',
      '> Loading projects... [████████████ 100%]',
      '> sudo hire ritesh --immediately',
      '> Verifying credentials...',
      '> ACCESS GRANTED ✓',
      '> Welcome, fellow developer 😎',
      '> Tip: Check the console for a secret message.',
    ];
    let i = 0;
    let done = false;
    const interval = setInterval(() => {
      if (done) return;
      if (i < seq.length) {
        const line = seq[i];
        i++;
        setLines(prev => [...prev, line]);
      } else {
        done = true;
        clearInterval(interval);
        setTimeout(onDone, 2000);
      }
    }, 600);
    return () => { done = true; clearInterval(interval); };
  }, [showTerminal, onDone]);

  return (
    <motion.div
      className="fixed inset-0 z-[9998] bg-black flex items-center justify-center p-6 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 opacity-40">
        <MatrixBackground />
      </div>

      <AnimatePresence>
        {showTerminal && (
          <motion.div 
            className="w-full max-w-2xl relative z-10"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
        <div className="bg-[#0a0a0a] border border-green-500/30 rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(0,255,65,0.15)]">
          <div className="flex items-center gap-2 px-4 py-3 bg-white/5 border-b border-white/5">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-green-500/80" />
            <span className="ml-2 font-mono text-[10px] text-white/30 tracking-widest">RITESH_PORTFOLIO — bash</span>
          </div>
          <div className="p-6 space-y-2 min-h-[240px]">
            {lines.filter(Boolean).map((line, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                className={`font-mono text-sm ${line.includes('GRANTED') ? 'text-green-400 font-bold text-base' : line.includes('sudo') ? 'text-yellow-300' : 'text-green-500/80'}`}
              >
                {line}
              </motion.p>
            ))}
            <motion.span
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="inline-block w-2 h-4 bg-green-400"
            />
          </div>
        </div>
      </motion.div>
      )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── RPG ACHIEVEMENT ─────────────────────────────────────────────────────────
function RPGAchievement({ onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3500); return () => clearTimeout(t); }, [onDone]);
  return (
    <motion.div
      className="fixed top-6 right-6 z-[9997] max-w-sm"
      initial={{ x: 120, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 120, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 200, damping: 20 }}
    >
      <div className="flex items-center gap-4 bg-gradient-to-r from-[#1a1200] to-[#1a0e00] border border-yellow-500/40 rounded-2xl px-5 py-4 shadow-[0_0_40px_rgba(234,179,8,0.25)] backdrop-blur-xl">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center text-2xl flex-shrink-0 shadow-lg">
          🏆
        </div>
        <div>
          <p className="text-[9px] font-mono tracking-[0.3em] uppercase text-yellow-500/70 mb-0.5">Achievement Unlocked</p>
          <p className="text-white font-bold text-sm leading-tight">Master Explorer</p>
          <p className="text-yellow-200/60 text-[10px] font-mono mt-0.5">You know the ancient code. Ritesh's hidden power: Coffee-Driven Development ☕</p>
        </div>
      </div>
    </motion.div>
  );
}

// ─── YOUTUBE POPUP ────────────────────────────────────────────────────────────
function YouTubePopup({ onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 6000); return () => clearTimeout(t); }, [onDone]);
  return (
    <motion.div
      className="fixed bottom-8 right-6 z-[9997] max-w-xs"
      initial={{ y: 40, opacity: 0, scale: 0.9 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      exit={{ y: 40, opacity: 0, scale: 0.9 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
    >
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
          <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
          </div>
          <div className="flex-1">
            <p className="text-white text-xs font-bold">Procoder09</p>
            <p className="text-gray-500 text-[10px]">YouTube</p>
          </div>
          <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
            <span className="text-white text-[8px] font-black">🔔</span>
          </div>
        </div>
        <div className="px-4 py-3">
          <p className="text-white text-xs leading-relaxed mb-3">
            🔔 <span className="font-semibold">New video just dropped!</span> Don't forget to subscribe.
          </p>
          <a
            href="https://youtube.com/@procoder09"
            target="_blank"
            rel="noreferrer"
            className="block w-full py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold text-center rounded-lg transition-colors"
          >
            Subscribe Now
          </a>
        </div>
      </div>
      <button onClick={onDone} className="absolute top-2 right-2 text-gray-500 hover:text-white text-xs p-1">✕</button>
    </motion.div>
  );
}

// ─── SELF DESTRUCT COUNTDOWN ──────────────────────────────────────────────────
function SelfDestruct({ onDone }) {
  const [count, setCount] = useState(10);
  const [exploded, setExploded] = useState(false);

  useEffect(() => {
    if (count <= 0) { setExploded(true); setTimeout(onDone, 3000); return; }
    const t = setTimeout(() => setCount(c => c - 1), 800);
    return () => clearTimeout(t);
  }, [count, onDone]);

  return (
    <motion.div
      className="fixed inset-0 z-[9998] bg-black/95 flex flex-col items-center justify-center gap-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {!exploded ? (
        <>
          <p className="font-mono text-red-400 text-[10px] tracking-[0.5em] uppercase animate-pulse">⚠ SELF DESTRUCT SEQUENCE INITIATED ⚠</p>
          <motion.div
            key={count}
            initial={{ scale: 1.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-[8rem] font-black text-red-500 font-mono"
            style={{ textShadow: '0 0 60px rgba(239,68,68,0.8)' }}
          >
            {count}
          </motion.div>
          <p className="text-red-400/50 font-mono text-xs tracking-widest">TICK... TICK...</p>
        </>
      ) : (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.2, 1] }}
          className="text-center space-y-4"
        >
          <div className="text-6xl">💥</div>
          <p className="text-white text-2xl font-black" style={{ fontFamily: "'Syne', sans-serif" }}>Just kidding 😄</p>
          <p className="text-gray-400 text-sm max-w-xs text-center leading-relaxed">
            — but seriously, I'm available for hire.<br />
            <span className="text-cyan-400 font-mono">Let's build something real.</span>
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}

// ─── STILL THERE IDLE HINT ────────────────────────────────────────────────────
function StillThereHint({ onDone }) {
  return (
    <motion.div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9995] pointer-events-none"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
    >
      <div className="flex items-center gap-3 px-5 py-3 bg-[#0c0d10]/90 border border-white/10 rounded-2xl backdrop-blur-xl shadow-2xl">
        <span className="text-sm">👀</span>
        <div>
          <p className="text-white text-xs font-bold">psst... still reading?</p>
          <p className="text-gray-500 font-mono text-[10px] mt-0.5">I see you. Try pressing ↑↑↓↓←→←→BA 😉</p>
        </div>
        <button onClick={onDone} className="pointer-events-auto text-gray-600 hover:text-white text-xs ml-2">✕</button>
      </div>
    </motion.div>
  );
}

// ─── ORIGIN STORY (Mobile long-press) ────────────────────────────────────────
function OriginStory({ onDone }) {
  return (
    <motion.div
      className="fixed inset-x-4 bottom-4 z-[9997]"
      initial={{ y: '100%', opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: '100%', opacity: 0 }}
      transition={{ type: 'spring', stiffness: 280, damping: 26 }}
    >
      <div className="bg-[#0c0d10] border border-white/10 rounded-3xl p-6 shadow-[0_-20px_60px_rgba(0,0,0,0.8)] backdrop-blur-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-lg">📖</span>
            <p className="text-white font-bold text-sm">The Origin Story</p>
          </div>
          <button onClick={onDone} className="text-gray-500 hover:text-white text-sm">✕</button>
        </div>
        <p className="text-gray-300 text-sm leading-relaxed">
          Started coding at 16 → Broke his first website in 2 hours → Never stopped 🚀
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {['React', 'Python', 'WordPress', 'YouTube'].map(t => (
            <span key={t} className="px-2 py-1 bg-white/5 border border-white/10 rounded-full text-[10px] text-gray-400 font-mono">{t}</span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// ─── PARTY MODE OVERLAY ───────────────────────────────────────────────────────
function PartyMode({ onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3500); return () => clearTimeout(t); }, [onDone]);
  const confettiCount = 30;
  return (
    <motion.div
      className="fixed inset-0 z-[9996] pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Disco gradient background */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(236,72,153,0.1))',
            'linear-gradient(135deg, rgba(6,182,212,0.15), rgba(16,185,129,0.1))',
            'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(239,68,68,0.1))',
            'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(236,72,153,0.1))',
          ]
        }}
        transition={{ duration: 1.5, repeat: Infinity }}
      />
      {/* Confetti */}
      {Array.from({ length: confettiCount }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 rounded-sm"
          style={{
            left: `${Math.random() * 100}%`,
            top: `-10px`,
            background: `hsl(${Math.random() * 360}, 80%, 65%)`,
            rotate: Math.random() * 360,
          }}
          animate={{
            y: window.innerHeight + 50,
            rotate: [0, 360],
            opacity: [1, 1, 0],
          }}
          transition={{
            duration: 2 + Math.random() * 1.5,
            delay: Math.random() * 0.8,
            ease: 'easeIn',
          }}
        />
      ))}
    </motion.div>
  );
}

// ─── EMOJI RAIN (Mobile swipe) ────────────────────────────────────────────────
function EmojiRain({ onDone }) {
  const emojis = ['🚀', '🎯', '💻', '⭐', '🔥', '✨', '🎉', '💡'];
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className="fixed inset-0 z-[9996] pointer-events-none">
      {Array.from({ length: 25 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute text-2xl"
          style={{ left: `${Math.random() * 95}%`, top: '-40px', fontSize: `${16 + Math.random() * 14}px` }}
          animate={{ y: window.innerHeight + 60, rotate: [0, Math.random() > 0.5 ? 360 : -360], opacity: [1, 1, 0] }}
          transition={{ duration: 1.5 + Math.random(), delay: Math.random() * 1, ease: 'easeIn' }}
        >
          {emojis[Math.floor(Math.random() * emojis.length)]}
        </motion.div>
      ))}
    </div>
  );
}

// ─── NIGHT OWL MOON ────────────────────────────────────────────────────────────
function NightOwlMoon({ onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.div
      className="fixed bottom-24 right-6 sm:bottom-12 sm:right-12 z-[9990]"
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0 }}
    >
      {!expanded ? (
        <button
          onClick={() => setExpanded(true)}
          className="w-12 h-12 rounded-full bg-[#0c1015] border border-white/20 flex items-center justify-center text-xl shadow-lg hover:border-indigo-400/50 transition-all"
          title="You've been here a while..."
        >
          🌙
        </button>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#0c0d10] border border-indigo-500/30 rounded-2xl p-4 max-w-[220px] shadow-xl"
        >
          <div className="flex justify-between items-start mb-2">
            <span className="text-base">🌙</span>
            <button onClick={onDismiss} className="text-gray-600 hover:text-white text-xs">✕</button>
          </div>
          <p className="text-white text-xs font-bold mb-1">Night Owl Mode</p>
          <p className="text-gray-400 text-[10px] leading-relaxed">
            You've been here a while... I respect that focus 💡
          </p>
          <p className="text-indigo-400 text-[10px] font-mono mt-2">{"// Keep exploring."}</p>
        </motion.div>
      )}
    </motion.div>
  );
}

// ─── FIRST VISIT GUIDE ────────────────────────────────────────────────────────
const FIRST_VISIT_KEY = 'rr_first_visited';

function FirstVisitGuide({ onDismiss }) {
  const [phase, setPhase] = useState(0); // 0=intro, 1=hint, 2=done
  const [displayText, setDisplayText] = useState('');
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const lines = isMobile
    ? [
      { text: "This isn't just a portfolio.", delay: 0 },
      { text: "There are hidden secrets waiting for you.", delay: 1200 },
      { text: "Try triple-tapping my photo 👆", delay: 2600 },
      { text: "Or long-press my name to hear my origin story 📖", delay: 4200 },
    ]
    : [
      { text: "Wait. This isn't a simple portfolio.", delay: 0 },
      { text: "There are 20 hidden easter eggs buried inside.", delay: 1400 },
      { text: "Start here — type  HACKER  on your keyboard.", delay: 3000 },
      { text: "Then explore on your own. Trust me. 😉", delay: 4800 },
    ];

  useEffect(() => {
    const timers = lines.map(({ text, delay }) =>
      setTimeout(() => {
        setDisplayText(text);
        setPhase(p => p + 1);
      }, delay)
    );
    // No auto-dismiss — stays until first egg is found
    return () => { timers.forEach(clearTimeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      className="fixed inset-0 z-[9994] flex items-center justify-center pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.4 } }}
    >
      {/* Subtle dark vignette, doesn't block interactions */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px]" />

      <motion.div
        className="relative pointer-events-auto max-w-md w-full mx-4"
        initial={{ y: 20, opacity: 0, scale: 0.96 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: -12, opacity: 0, scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 200, damping: 22 }}
      >
        {/* Glow halo */}
        <motion.div
          className="absolute -inset-4 rounded-3xl pointer-events-none"
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
          style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.2) 0%, transparent 70%)' }}
        />

        <div className="relative bg-[#08090c]/90 border border-white/10 rounded-3xl px-7 py-6 shadow-[0_20px_80px_rgba(0,0,0,0.8)] backdrop-blur-2xl overflow-hidden">
          {/* Top shimmer line */}
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-500/60 to-transparent" />

          {/* Icon row */}
          <div className="flex items-center gap-3 mb-4">
            <motion.div
              className="w-10 h-10 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-600/10 border border-cyan-500/30 flex items-center justify-center text-xl"
              animate={{ rotate: [0, 8, -8, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 1 }}
            >
              🥚
            </motion.div>
            <div>
              <p className="text-[9px] font-mono tracking-[0.35em] uppercase text-cyan-400/70">Easter Egg System</p>
              <p className="text-white font-black text-sm" style={{ fontFamily: "'Syne', sans-serif" }}>
                You're being watched 👀
              </p>
            </div>
            <button
              onClick={onDismiss}
              className="ml-auto text-gray-600 hover:text-white transition-colors text-xs p-1"
            >
              Skip ✕
            </button>
          </div>

          {/* Animated message lines */}
          <div className="space-y-3 min-h-[90px]">
            {lines.map(({ text }, idx) => (
              <AnimatePresence key={idx}>
                {phase > idx && (
                  <motion.div
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-start gap-2"
                  >
                    <span className="text-cyan-400 font-mono text-xs mt-0.5 flex-shrink-0">›</span>
                    <p className={`text-sm leading-relaxed ${idx === 0 ? 'text-white font-semibold' :
                        idx === lines.length - 1 ? 'text-cyan-300' :
                          'text-gray-300'
                      }`}>
                      {text}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            ))}
          </div>

          {/* Keyboard hint (desktop only) */}
          {!isMobile && phase >= 2 && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-4 pt-4 border-t border-white/5 flex items-center gap-2 flex-wrap"
            >
              {['H', 'A', 'C', 'K', 'E', 'R'].map((k, i) => (
                <motion.kbd
                  key={k}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="px-2 py-1 bg-white/5 border border-white/15 rounded-md text-white font-mono text-xs font-bold shadow-sm"
                >
                  {k}
                </motion.kbd>
              ))}
              <motion.span
                animate={{ opacity: [1, 0.4, 1] }}
                transition={{ duration: 1.4, repeat: Infinity }}
                className="text-cyan-400 text-[10px] font-mono ml-1"
              >
                ← type this
              </motion.span>
            </motion.div>
          )}

          {/* Progress dots showing phases */}
          <div className="flex items-center gap-1.5 mt-4">
            {lines.map((_, i) => (
              <motion.div
                key={i}
                className="h-1 rounded-full bg-white/10"
                animate={{ width: phase > i ? 20 : 6, backgroundColor: phase > i ? 'rgba(6,182,212,0.8)' : 'rgba(255,255,255,0.1)' }}
                transition={{ duration: 0.3 }}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── PREMIUM EGG TOAST (top-center) ──────────────────────────────────────────
function EggToast({ egg, total, index, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <motion.div
      layout
      initial={{ y: -90, opacity: 0, scale: 0.9 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      exit={{ y: -90, opacity: 0, scale: 0.9 }}
      transition={{ type: 'spring', stiffness: 280, damping: 24 }}
      className="flex flex-col gap-2"
    >
      <div className="flex items-center gap-3 bg-[#0c0d12] border border-white/10 rounded-2xl px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.6)] backdrop-blur-2xl min-w-[280px] max-w-sm relative overflow-hidden">
        {/* Animated shimmer */}
        <motion.div
          className="absolute inset-0 -translate-x-full"
          animate={{ translateX: ['−100%', '200%'] }}
          transition={{ duration: 1.2, delay: 0.1 }}
          style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)' }}
        />

        {/* Egg icon with pulse ring */}
        <div className="relative flex-shrink-0">
          <motion.div
            className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400/20 to-orange-500/10 border border-amber-400/30 flex items-center justify-center text-lg"
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 1, repeat: 2 }}
          >
            🥚
          </motion.div>
          <motion.div
            className="absolute inset-0 rounded-xl border border-amber-400/50"
            animate={{ scale: [1, 1.5], opacity: [0.6, 0] }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-xs">Hidden Egg Found!</p>
          <p className="text-gray-400 text-[10px] font-mono mt-0.5 truncate">{egg.label}</p>
          {/* Progress pip track */}
          <div className="flex items-center gap-1.5 mt-2">
            <div className="flex gap-0.5">
              {Array.from({ length: total }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1 rounded-full transition-all duration-300 ${i < index ? 'bg-amber-400 w-3' : 'bg-white/10 w-1.5'}`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 text-right">
          <span className="text-amber-400 font-black text-lg leading-none">{index}</span>
          <span className="text-gray-600 text-xs font-mono">/{total}</span>
        </div>

        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-600 hover:text-white text-[10px] transition-colors"
        >
          ✕
        </button>
      </div>

      {/* All-found celebration */}
      {index === total && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-amber-500/20 to-orange-500/10 border border-amber-400/30 rounded-xl px-4 py-2 text-center"
        >
          <p className="text-amber-300 text-[11px] font-bold">🏆 All eggs discovered! You're a true explorer.</p>
        </motion.div>
      )}
    </motion.div>
  );
}

// ─── MAIN ENGINE ──────────────────────────────────────────────────────────────
export default function EasterEggEngine() {
  const [foundEggs, setFoundEggs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch { return []; }
  });
  const [activeToast, setActiveToast] = useState(null);
  const [activeOverlay, setActiveOverlay] = useState(null); // 'matrix'|'terminal'|'party'|'destruct'|'emoji-rain'|'origin'

  // Flags for transient effects that don't need overlay state
  const [showRPG, setShowRPG] = useState(false);
  const [showYT, setShowYT] = useState(false);
  const [showIdle, setShowIdle] = useState(false);
  const [showNightOwl, setShowNightOwl] = useState(false);
  const [isRetroMode, setIsRetroMode] = useState(false);
  const [showOrigin, setShowOrigin] = useState(false);
  const isFlippedRef = useRef(false);

  // First visit guide — show on every reload until the first egg is found
  // Uses a local session-skip so clicking Skip hides it for the current tab session only
  const [firstVisitSkipped, setFirstVisitSkipped] = useState(false);
  const showFirstVisit = foundEggs.length === 0 && !firstVisitSkipped;

  // Input tracking refs
  const keyBuffer = useRef([]);
  const konamiBuffer = useRef([]);
  const konamiCount = useRef(0); // 1st = matrix, 2nd = rpg
  const idleTimer = useRef(null);
  const nightOwlTimer = useRef(null);
  const logoHoverCount = useRef(0);
  const logoHoverTimer = useRef(null);

  // ── Touch tracking for mobile
  const touchStartY = useRef(null);
  const touchStartX = useRef(null);
  const swipeUpCount = useRef(0);
  const swipeDownCount = useRef(0);
  const swipeLeftCount = useRef(0);
  const swipeRightCount = useRef(0);
  const swipeTimer = useRef(null);
  const profileTapCount = useRef(0);
  const profileTapTimer = useRef(null);
  const longPressTimer = useRef(null);
  const lastTapTime = useRef(0);

  // Persist found eggs
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(foundEggs));
  }, [foundEggs]);

  // Inject console message once
  useEffect(() => { injectConsoleMessage(); }, []);

  // ── TRIGGER EGG ──────────────────────────────────────────────────────────────
  const triggerEgg = useCallback((id, label) => {
    setFoundEggs(prev => {
      const isNew = !prev.includes(id);
      const next = isNew ? [...prev, id] : prev;
      
      // Always show the toast even if found before so user feels the feedback
      setTimeout(() => {
        setActiveToast({ id, label, index: next.length, ts: Date.now() });
      }, 0);
      
      return next;
    });
  }, []);

  const closeToast = useCallback(() => setActiveToast(null), []);

  // ── KONAMI / KEY SEQUENCE DETECTION ──────────────────────────────────────────
  useEffect(() => {
    const words = {
      RITESH: () => { triggerEgg('ritesh', 'Party Mode — You found me! 🎉'); setActiveOverlay('party'); },
      HIRE: () => { triggerEgg('hire', 'Hire Mode — Great decision! 👔'); scrollToSection('Contact'); setTimeout(() => { const el = document.getElementById('Contact'); if (el) { el.style.transition = 'box-shadow 0.4s'; el.style.boxShadow = '0 0 40px 4px rgba(251,191,36,0.3)'; setTimeout(() => { el.style.boxShadow = ''; }, 2500); } }, 300); },
      HACKER: () => { triggerEgg('hacker', 'Hacker Terminal Unlocked 💻'); setActiveOverlay('terminal'); },
      PROCODER: () => { triggerEgg('procoder', 'YouTube Notification 🔔'); setShowYT(true); },
      PIXEL: () => { triggerEgg('pixel', 'Retro 8-bit Mode Activated 🕹️'); setIsRetroMode(r => { const next = !r; document.documentElement.classList.toggle('retro-mode', next); return next; }); },
      FLIP: () => {
        if (isFlippedRef.current) return;
        triggerEgg('flip', 'Page Flip 🙃 — Press any key to restore');
        isFlippedRef.current = true;
        document.body.style.transition = 'transform 0.8s ease';
        document.body.style.transform = 'rotate(180deg)';
        const resetFlip = () => {
          document.body.style.transform = '';
          isFlippedRef.current = false;
          window.removeEventListener('keydown', resetFlip);
        };
        // Reset on next keypress (with small delay so the triggering FLIP key doesn't instantly reset)
        setTimeout(() => window.addEventListener('keydown', resetFlip, { once: true }), 300);
      },
      BOOM: () => { triggerEgg('boom', 'Self-Destruct Initiated 💣'); setActiveOverlay('destruct'); },
      ANIME: () => { triggerEgg('anime', 'Mangekyou Sharingan — Amaterasu! 👁️'); setActiveOverlay('mangekou'); },
      ITACHI: () => { triggerEgg('itachi', 'Itachi’s Eye Awakened 🩸'); setActiveOverlay('mangekou'); },
    };

    const onKeyDown = (e) => {
      const key = e.key;

      // Konami buffer
      konamiBuffer.current = [...konamiBuffer.current, key].slice(-10);
      if (konamiBuffer.current.join(',') === KONAMI.join(',')) {
        konamiBuffer.current = [];
        konamiCount.current += 1;
        if (konamiCount.current % 2 === 1) {
          triggerEgg('matrix', 'Classic Konami Code — Matrix Rain 🌧️');
          setActiveOverlay('matrix');
        } else {
          triggerEgg('rpg', 'Konami Code — RPG Achievement Unlocked 🏆');
          setShowRPG(true);
        }
        return;
      }

      // Word buffer
      if (key.length === 1) {
        keyBuffer.current = [...keyBuffer.current, key.toUpperCase()].slice(-10);
        const buf = keyBuffer.current.join('');
        for (const [word, action] of Object.entries(words)) {
          if (buf.endsWith(word)) { action(); keyBuffer.current = []; break; }
        }
      }

      // Reset idle timer
      resetIdle();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerEgg]);

  // ── IDLE DETECTION ────────────────────────────────────────────────────────────
  const resetIdle = useCallback(() => {
    clearTimeout(idleTimer.current);
    setShowIdle(false);
    idleTimer.current = setTimeout(() => { setShowIdle(true); triggerEgg('idle', 'Idle 30s — Still there? 👀'); }, 30000);
  }, [triggerEgg]);

  useEffect(() => {
    resetIdle();
    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('touchstart', resetIdle);
    return () => {
      clearTimeout(idleTimer.current);
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('touchstart', resetIdle);
    };
  }, [resetIdle]);

  // ── NIGHT OWL (60s on page) ───────────────────────────────────────────────────
  useEffect(() => {
    nightOwlTimer.current = setTimeout(() => {
      setShowNightOwl(true);
      triggerEgg('nightowl', 'Night Owl — 60s on page 🌙');
    }, 60000);
    return () => clearTimeout(nightOwlTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── LOGO HOVER COUNTER ────────────────────────────────────────────────────────
  useEffect(() => {
    const logoEl = document.querySelector('[data-egg-logo]');
    if (!logoEl) return;

    const handleEnter = () => {
      logoHoverCount.current += 1;
      clearTimeout(logoHoverTimer.current);
      logoHoverTimer.current = setTimeout(() => { logoHoverCount.current = 0; }, 3000);
      if (logoHoverCount.current >= 5) {
        logoHoverCount.current = 0;
        triggerEgg('logoHover', 'Logo Hover 5× — Confetti Burst 🎊');
        setActiveOverlay('party');
      }
    };

    logoEl.addEventListener('mouseenter', handleEnter);
    return () => logoEl.removeEventListener('mouseenter', handleEnter);
  }, [triggerEgg]);

  // ── PROFILE PHOTO HOVER 3s ────────────────────────────────────────────────────
  useEffect(() => {
    const photo = document.querySelector('[data-egg-photo]');
    if (!photo) return;
    let hoverTimer;

    const onEnter = () => {
      hoverTimer = setTimeout(() => {
        triggerEgg('photoHover', 'Profile Photo Stare — Noticed 👀');
        photo.style.transition = 'all 0.4s';
        photo.style.animation = 'eggPhotoBounce 0.6s ease infinite alternate';
        photo.style.filter = 'brightness(1.15) saturate(1.3)';
        setTimeout(() => { photo.style.animation = ''; photo.style.filter = ''; }, 2500);
      }, 3000);
    };
    const onLeave = () => clearTimeout(hoverTimer);

    photo.addEventListener('mouseenter', onEnter);
    photo.addEventListener('mouseleave', onLeave);
    return () => { photo.removeEventListener('mouseenter', onEnter); photo.removeEventListener('mouseleave', onLeave); };
  }, [triggerEgg]);

  // ── MOBILE: Triple tap profile photo ─────────────────────────────────────────
  useEffect(() => {
    const photo = document.querySelector('[data-egg-photo]');
    if (!photo) return;

    const onTouch = () => {
      profileTapCount.current += 1;
      clearTimeout(profileTapTimer.current);
      profileTapTimer.current = setTimeout(() => { profileTapCount.current = 0; }, 1000);
      if (profileTapCount.current >= 3) {
        profileTapCount.current = 0;
        triggerEgg('tripleTap', 'Triple Tap — Photo Dances 💃');
        photo.style.transition = 'all 0.3s';
        photo.style.animation = 'eggPhotoDance 1.2s ease';
        setTimeout(() => { photo.style.animation = ''; }, 1500);
      }
    };

    photo.addEventListener('touchend', onTouch);
    return () => photo.removeEventListener('touchend', onTouch);
  }, [triggerEgg]);

  // ── MOBILE: Long press on name ────────────────────────────────────────────────
  useEffect(() => {
    const nameEl = document.querySelector('[data-egg-name]');
    if (!nameEl) return;

    const start = (e) => {
      e.preventDefault();
      longPressTimer.current = setTimeout(() => {
        triggerEgg('longPressName', 'Long Press — Origin Story Revealed 📖');
        setShowOrigin(true);
      }, 2000);
    };
    const cancel = () => clearTimeout(longPressTimer.current);

    nameEl.addEventListener('touchstart', start, { passive: false });
    nameEl.addEventListener('touchend', cancel);
    nameEl.addEventListener('touchmove', cancel);
    return () => {
      nameEl.removeEventListener('touchstart', start);
      nameEl.removeEventListener('touchend', cancel);
      nameEl.removeEventListener('touchmove', cancel);
    };
  }, [triggerEgg]);

  // ── MOBILE: Double-tap logo ───────────────────────────────────────────────────
  useEffect(() => {
    const logoEl = document.querySelector('[data-egg-logo]');
    if (!logoEl) return;

    const onTouch = () => {
      const now = Date.now();
      if (now - lastTapTime.current < 300) {
        triggerEgg('doubleTapLogo', 'Double-Tap Logo — You\'re Curious! 🚀');
        logoEl.style.transition = 'transform 0.6s ease';
        logoEl.style.animation = 'eggLogoSpin 0.6s ease';
        setTimeout(() => { logoEl.style.animation = ''; }, 700);
      }
      lastTapTime.current = now;
    };

    logoEl.addEventListener('touchend', onTouch);
    return () => logoEl.removeEventListener('touchend', onTouch);
  }, [triggerEgg]);

  // ── MOBILE: Global Gestures (Swipes & Multi-finger taps) ────────────────────
  useEffect(() => {
    let tapTimeout = null;
    
    const onTouchStart = (e) => { 
      touchStartY.current = e.touches[0].clientY; 
      touchStartX.current = e.touches[0].clientX; 
      
      // Multi-finger taps
      if (e.touches.length === 2) {
        tapTimeout = setTimeout(() => {
          triggerEgg('twoFinger', 'Two Finger Tap — Destruct Sequence! ✌️💣');
          setActiveOverlay('destruct');
        }, 300);
      } else if (e.touches.length === 3) {
        clearTimeout(tapTimeout);
        triggerEgg('threeFinger', 'Three Finger Tap — Amaterasu! 🖖👁️');
        setActiveOverlay('mangekou');
      }
    };
    
    const onTouchEnd = (e) => {
      clearTimeout(tapTimeout);
      if (!touchStartY.current || !touchStartX.current || e.changedTouches.length === 0) return;
      
      const deltaY = e.changedTouches[0].clientY - touchStartY.current;
      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      
      // Determine swipe direction
      if (absY > 80 && absY > absX) {
        if (deltaY < 0) {
          // Swiped UP
          swipeUpCount.current += 1;
          clearTimeout(swipeTimer.current);
          swipeTimer.current = setTimeout(() => { swipeUpCount.current = 0; }, 2000);
          if (swipeUpCount.current >= 3) {
            swipeUpCount.current = 0;
            triggerEgg('swipeUp', 'Swipe Up 3× — Emoji Rain! 🎊');
            setActiveOverlay('emoji-rain');
          }
        } else {
          // Swiped DOWN
          swipeDownCount.current += 1;
          clearTimeout(swipeTimer.current);
          swipeTimer.current = setTimeout(() => { swipeDownCount.current = 0; }, 2000);
          if (swipeDownCount.current >= 3) {
            swipeDownCount.current = 0;
            triggerEgg('swipeDown', 'Swipe Down 3× — Terminal Accessed 💻');
            setActiveOverlay('terminal');
          }
        }
      } else if (absX > 80 && absX > absY) {
        if (deltaX < 0) {
          // Swiped LEFT
          swipeLeftCount.current += 1;
          clearTimeout(swipeTimer.current);
          swipeTimer.current = setTimeout(() => { swipeLeftCount.current = 0; }, 2000);
          if (swipeLeftCount.current >= 3) {
            swipeLeftCount.current = 0;
            triggerEgg('swipeLeft', 'Swipe Left 3× — Retro Mode 🕹️');
            setIsRetroMode(r => { 
              const next = !r; 
              document.documentElement.classList.toggle('retro-mode', next); 
              return next; 
            });
          }
        } else {
          // Swiped RIGHT
          swipeRightCount.current += 1;
          clearTimeout(swipeTimer.current);
          swipeTimer.current = setTimeout(() => { swipeRightCount.current = 0; }, 2000);
          if (swipeRightCount.current >= 3) {
            swipeRightCount.current = 0;
            if (!isFlippedRef.current) {
              triggerEgg('swipeRight', 'Swipe Right 3× — World Flipped 🙃');
              isFlippedRef.current = true;
              document.body.style.transition = 'transform 0.8s ease';
              document.body.style.transform = 'rotate(180deg)';
              const resetFlip = () => {
                document.body.style.transform = '';
                isFlippedRef.current = false;
                window.removeEventListener('keydown', resetFlip);
                window.removeEventListener('touchstart', resetFlip);
              };
              setTimeout(() => {
                window.addEventListener('keydown', resetFlip, { once: true });
                window.addEventListener('touchstart', resetFlip, { once: true });
              }, 400);
            }
          }
        }
      }
      touchStartY.current = null;
      touchStartX.current = null;
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [triggerEgg]);

  const closeOverlay = useCallback(() => setActiveOverlay(null), []);

  // ── RENDER ────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Premium Egg Toast (top-center) */}
      <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none flex flex-col items-center gap-2">
        <AnimatePresence mode="wait">
          {activeToast && (
            <div className="pointer-events-auto" key={`${activeToast.id}-${activeToast.ts}`}>
              <EggToast
                egg={activeToast}
                total={TOTAL_EGGS}
                index={activeToast.index}
                onClose={closeToast}
              />
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Overlays */}
      <AnimatePresence>
        {activeOverlay === 'matrix' && <MatrixRain key="matrix" onDone={closeOverlay} />}
        {activeOverlay === 'terminal' && <HackerTerminal key="terminal" onDone={closeOverlay} />}
        {activeOverlay === 'party' && <PartyMode key="party" onDone={closeOverlay} />}
        {activeOverlay === 'destruct' && <SelfDestruct key="destruct" onDone={closeOverlay} />}
        {activeOverlay === 'emoji-rain' && <EmojiRain key="emoji-rain" onDone={closeOverlay} />}
        {activeOverlay === 'mangekou' && <MangekouEgg key="mangekou" onDone={closeOverlay} />}
        {showRPG && <RPGAchievement key="rpg" onDone={() => setShowRPG(false)} />}
        {showYT && <YouTubePopup key="yt" onDone={() => setShowYT(false)} />}
        {showIdle && <StillThereHint key="idle" onDone={() => setShowIdle(false)} />}
        {showNightOwl && <NightOwlMoon key="nightowl" onDismiss={() => setShowNightOwl(false)} />}
        {showOrigin && <OriginStory key="origin" onDone={() => setShowOrigin(false)} />}
        {showFirstVisit && <FirstVisitGuide key="firstvisit" onDismiss={() => setFirstVisitSkipped(true)} />}
      </AnimatePresence>
    </>
  );
}
